import { WEIGHT_UNIT_GRAMS, WEIGHT_UNIT_KILOGRAMS, WEIGHT_UNIT_OUNCES, WEIGHT_UNIT_POUNDS } from '../types/weight';

import type { BaseWeightUnit, Weight, WeightUnit } from '../types/weight';

const OUNCES_PER_POUND = 16;
const GRAMS_PER_KILOGRAM = 1000;
const DECIMAL_PRECISION = 100000;

const GRAMS_PER_UNIT: Record<WeightUnit, number> = {
  Grams: 1,
  Kilograms: 1000,
  Ounces: 28.3495,
  Pounds: 453.592,
};

/**
 * Convert a weight value between any two weight units.
 * Uses grams as an intermediate to avoid a 4×4 conversion matrix.
 */
export const convertWeight = (value: number, fromUnit: WeightUnit, toUnit: WeightUnit): number => {
  if (fromUnit === toUnit) return value;
  const grams = value * GRAMS_PER_UNIT[fromUnit];
  return grams / GRAMS_PER_UNIT[toUnit];
};

/** Split total ounces into pounds and remaining ounces */
export const splitOuncesToPoundsAndOunces = (totalOz: number): { pounds: number; ounces: number } => {
  const rounded = Math.round(totalOz * DECIMAL_PRECISION) / DECIMAL_PRECISION;
  const pounds = Math.floor(rounded / OUNCES_PER_POUND);
  const ounces = Math.round((rounded % OUNCES_PER_POUND) * DECIMAL_PRECISION) / DECIMAL_PRECISION;
  return { pounds, ounces };
};

/** Combine pounds and ounces into total ounces */
export const combinePoundsAndOunces = (pounds: number, ounces: number): number => {
  return Math.max(pounds * OUNCES_PER_POUND + ounces, 0);
};

/** Split total grams into kilograms and remaining grams */
export const splitGramsToKilogramsAndGrams = (totalGrams: number): { kilograms: number; grams: number } => {
  const rounded = Math.round(totalGrams * DECIMAL_PRECISION) / DECIMAL_PRECISION;
  const kilograms = Math.floor(rounded / GRAMS_PER_KILOGRAM);
  const grams = Math.round((rounded % GRAMS_PER_KILOGRAM) * DECIMAL_PRECISION) / DECIMAL_PRECISION;
  return { kilograms, grams };
};

/** Combine kilograms and grams into total grams */
export const combineKilogramsAndGrams = (kilograms: number, grams: number): number => {
  return Math.max(kilograms * GRAMS_PER_KILOGRAM + grams, 0);
};

/**
 * Normalizes an API weight (any WeightUnit) to a base unit (Ounces or Grams).
 * Pounds converts to Ounces, Kilograms converts to Grams with numeric scaling.
 */
export const normalizeApiWeight = (
  apiWeight: { value?: number; unit?: string } | null | undefined,
  targetBaseUnit: BaseWeightUnit = WEIGHT_UNIT_OUNCES,
): { value: number; unit: BaseWeightUnit } => {
  if (!apiWeight?.unit) return { value: 0, unit: targetBaseUnit };

  const value = apiWeight.value ?? 0;
  const { unit } = apiWeight;

  if (unit === WEIGHT_UNIT_OUNCES) return { value, unit: WEIGHT_UNIT_OUNCES };
  if (unit === WEIGHT_UNIT_GRAMS) return { value, unit: WEIGHT_UNIT_GRAMS };
  if (unit === WEIGHT_UNIT_POUNDS) return { value: convertWeight(value, WEIGHT_UNIT_POUNDS, WEIGHT_UNIT_OUNCES), unit: WEIGHT_UNIT_OUNCES };
  if (unit === WEIGHT_UNIT_KILOGRAMS)
    return { value: convertWeight(value, WEIGHT_UNIT_KILOGRAMS, WEIGHT_UNIT_GRAMS), unit: WEIGHT_UNIT_GRAMS };

  console.warn(`normalizeApiWeight: unknown unit "${unit}", falling back to ${targetBaseUnit}`);
  return { value, unit: targetBaseUnit };
};

/** Returns true if the weight unit is imperial (Ounces or Pounds). */
export const isImperialWeight = (unit: Weight['unit']): boolean => unit === WEIGHT_UNIT_OUNCES || unit === WEIGHT_UNIT_POUNDS;

/** Splits a base-unit weight value into major/minor for display (lb+oz or kg+g). */
export const computeWeightSplit = (value: number, isImperial: boolean): { major: number; minor: number } => {
  if (isImperial) {
    const { pounds, ounces } = splitOuncesToPoundsAndOunces(value);
    return { major: pounds, minor: ounces };
  }
  const { kilograms, grams } = splitGramsToKilogramsAndGrams(value);
  return { major: kilograms, minor: grams };
};
